/**
 * Open / Fold preflop quiz. Procedural — every hand from sheet 15.
 */

import { PlayingCard } from "@/features/chat/components/PlayingCard";
import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  allHands,
  bucketForPosition,
  bucketSummary,
  isInOpenRange,
  parseHand,
  POSITIONS,
  type HandNotation,
  type Position,
} from "@/features/quiz/lib/preflopRanges";
import { pickFrom } from "@/features/quiz/lib/common";

type Answer = "open" | "fold";

interface Question {
  position: Position;
  hand: HandNotation;
  /** Card tokens rendered by PlayingCard. Random suits per question so the
   *  same hand notation visually varies across reps. */
  cards: [string, string];
}

const HANDS = allHands();

function pickQuestion(): Question {
  const position = pickFrom(POSITIONS);
  const hand = pickFrom(HANDS);
  return { position, hand, cards: cardsForHand(hand) };
}

function cardsForHand(hand: HandNotation): [string, string] {
  const parsed = parseHand(hand);
  if (!parsed) return ["Ah", "Ah"];
  const suits = ["s", "h", "d", "c"];
  if (parsed.isPair) {
    const i = Math.floor(Math.random() * 4);
    const j = (i + 1 + Math.floor(Math.random() * 3)) % 4;
    return [`${parsed.high}${suits[i]}`, `${parsed.low}${suits[j]}`];
  }
  if (parsed.suited) {
    const i = Math.floor(Math.random() * 4);
    return [`${parsed.high}${suits[i]}`, `${parsed.low}${suits[i]}`];
  }
  const i = Math.floor(Math.random() * 4);
  const j = (i + 1 + Math.floor(Math.random() * 3)) % 4;
  return [`${parsed.high}${suits[i]}`, `${parsed.low}${suits[j]}`];
}

export function QuizOpenFold() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<Question, Answer>({
      quizType: "open_fold",
      generate: pickQuestion,
      getCorrectAnswer: (q) => (isInOpenRange(q.position, q.hand) ? "open" : "fold"),
      getQuestionPayload: (q) => ({ position: q.position, hand: q.hand }),
    });

  const bucket = bucketForPosition(question.position);

  return (
    <QuizLayout
      Icon={Brain}
      title="Open / Fold"
      subtitle="Preflop range trainer: sheet 15"
      description="Tests whether you have memorized standard 9-handed opening ranges by position. Most poker EV lives preflop: opening too wide from early position or folding profitable hands from late position is the most common low-stakes leak."
      stats={stats}
    >
      <div className="flex items-center justify-center gap-4">
        <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent/15 text-accent text-sm font-semibold tracking-wide">
          {question.position}
        </span>
        <span className="text-text-muted text-xs">·  9-handed  ·  100bb</span>
      </div>

      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center gap-2">
          <PlayingCard token={question.cards[0]} size="lg" />
          <PlayingCard token={question.cards[1]} size="lg" />
        </div>
        <div className="text-xs text-text-muted font-mono">{question.hand}</div>
      </div>

      <p className="text-center text-text-muted text-sm">
        Folded to you. Should you open?
      </p>

      <div className="grid grid-cols-2 gap-3">
        <QuizOption
          value={"fold" as Answer}
          revealedAnswer={revealedAnswer}
          correctAnswer={correctAnswer}
          onSelect={submit}
          size="lg"
        >
          Fold
        </QuizOption>
        <QuizOption
          value={"open" as Answer}
          revealedAnswer={revealedAnswer}
          correctAnswer={correctAnswer}
          onSelect={submit}
          size="lg"
        >
          Open
        </QuizOption>
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{correctAnswer === "open" ? "Open" : "Fold"}</strong> with {question.hand} from {question.position}.
            </>
          }
          rationale={bucketSummary(bucket)}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
